import React, { createContext, useState, useEffect, useContext } from 'react';
import groupService from '../api/groupService';
import { useAuth } from './AuthContext';

// Create the group context
const GroupContext = createContext();

// Custom hook to use the group context
export const useGroup = () => {
  return useContext(GroupContext);
};

// Group provider component
export const GroupProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [adminGroup, setAdminGroup] = useState(null);
  const [adminGroups, setAdminGroups] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Add state to track if admin had communities before token expiry
  const [adminHadCommunities, setAdminHadCommunities] = useState(false);
  const [previousAdminEmail, setPreviousAdminEmail] = useState(null);

  // Platform admin: detected when server returns scraped groups
  // (regular admins can never own scraped groups — only platform admin gets them)
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);

  // selectGroup: called from the picker modal
  const selectGroup = (group) => {
    setAdminGroup(group);
    setActiveGroupId(group.id);
  };

  // Fetch admin's group when the user is logged in as admin
  useEffect(() => {
    console.log('🔄 GroupContext useEffect triggered:', { 
      currentUserId: currentUser?.id, 
      currentUserEmail: currentUser?.email,
      hasToken: !!localStorage.getItem('token'),
      previousAdminEmail
    });
    
    const fetchGroups = async () => {
      try {
        setLoading(true);
        setError('');

        // 🚨 SMART SECURITY FIX: Only clear group data if this is a DIFFERENT user
        // Don't clear for the same admin during token refresh scenarios
        const isSameAdmin = currentUser?.email === previousAdminEmail && 
                           currentUser?.role === 'admin' && 
                           previousAdminEmail;
        
        // 🔧 CRITICAL FIX: Don't clear on initial load when previousAdminEmail is null
        const isInitialLoad = !previousAdminEmail && currentUser?.role === 'admin';
        
        if (!isSameAdmin && !isInitialLoad) {
          console.log('🔒 SECURITY: Different user detected, clearing all group context state');
          setAdminGroup(null);
          setAdminGroups([]);
          setActiveGroupId(null);
          setUserGroups([]);
          setAdminHadCommunities(false);
        } else if (isInitialLoad) {
          console.log('🔒 SECURITY: Initial load for admin, preserving any existing state');
        } else {
          console.log('🔒 SECURITY: Same admin during token refresh, preserving community indicators');
        }

        // Only fetch groups if user is fully authenticated AND has a valid token
        if (!currentUser || !localStorage.getItem('token')) {
          console.log('🔒 SECURITY: No authenticated user or token - clearing group context');
          // Only clear if no current user at all (complete logout)
          if (!currentUser) {
            setAdminHadCommunities(false);
            setPreviousAdminEmail(null);
          }
          setLoading(false);
          return;
        }

        // Track the current admin email for future comparisons
        if (currentUser.role === 'admin') {
          setPreviousAdminEmail(currentUser.email);
        }

        // 🔒 SECURITY: Validate that we're fetching data for the correct user
        console.log('🔒 SECURITY: Fetching groups for user:', currentUser.email);

        if (currentUser.role === 'admin') {
          try {
            console.log('🔒 SECURITY: Fetching admin groups...');
            const response = await groupService.getAdminGroups();
            const groups = response?.data?.data || response?.data || [];
            
            console.log(`🔒 SECURITY: Found ${groups.length} admin groups for ${currentUser.email}`);
            console.log('🔒 SECURITY: Admin groups data:', groups);
            
            // 🚨 TRACK COMMUNITY EXISTENCE: Remember if this admin has communities
            if (groups.length > 0) {
              setAdminHadCommunities(true);
            }
            
            // 🚨 EMERGENCY FIX: Force immediate state update with proper error handling
            setAdminGroups(groups);
            
            // Detect platform admin: server returns scraped groups only for platform admin
            const hasScrpedGroups = groups.some(g => g.source_type === 'scraped');
            if (hasScrpedGroups) {
              setIsPlatformAdmin(true);
              console.log('👑 PLATFORM ADMIN detected — skipping auto-group selection, showing picker');
            }

            // Set the first group as the active admin group
            // Platform admin skips auto-selection — they pick via the modal picker
            if (groups && groups.length > 0 && !hasScrpedGroups) {
              const firstGroup = groups[0];
              console.log('🔒 SECURITY: Setting active admin group:', firstGroup.name, 'ID:', firstGroup.id);
              setAdminGroup(firstGroup);
              setActiveGroupId(firstGroup.id);
              
              // 🚨 FORCE UPDATE: Wait a moment and verify the state was set correctly
              setTimeout(() => {
                console.log('🔒 SECURITY: Verifying state update completed for:', currentUser.email);
              }, 100);
            }
          } catch (groupError) {
            console.error('🔒 SECURITY: Error fetching admin groups:', groupError);
            
            // 🚨 CRITICAL FIX: If this is a 401/403 error and admin had communities before,
            // don't clear the indicator - this is likely just a token refresh issue
            if ((groupError.response?.status === 401 || groupError.response?.status === 403) && 
                adminHadCommunities && isSameAdmin) {
              console.log('🔒 SECURITY: Auth error for admin who had communities - preserving indicator');
              setError('Session expired. Please refresh your session to access your communities.');
            } else {
              setError('Failed to load admin groups');
            }
          }
        } else {
          try {
            console.log('🔒 SECURITY: Fetching member groups...');
            console.log('🔒 SECURITY: Current user data:', {
              id: currentUser.id,
              email: currentUser.email,
              phone: currentUser.phone,
              role: currentUser.role
            });
            console.log('🔒 SECURITY: Token in localStorage:', localStorage.getItem('token') ? 'EXISTS' : 'MISSING');
            
            const response = await groupService.getUserGroups();
            const groups = response?.data?.data || response?.data || [];
            
            console.log(`🔒 SECURITY: Found ${groups.length} member groups for ${currentUser.email || currentUser.phone}`);
            console.log('🔒 SECURITY: API Response:', response);
            setUserGroups(groups);
          } catch (groupError) {
            console.error('🔒 SECURITY: Error fetching user groups:', groupError);
            setError('Failed to load user groups');
          }
        }
      } catch (error) {
        console.error('🔒 SECURITY: Error in group context initialization:', error);
        setError('Failed to load group information');
      } finally {
        setLoading(false);
      }
    };

    // 🔒 CRITICAL: Only fetch if we have a currentUser, and re-run when currentUser changes
    if (currentUser) {
      console.log('🔒 SECURITY: User changed, fetching fresh group data for:', currentUser.email, currentUser.id);
      fetchGroups();
    } else {
      // If no current user, clear all group state
      console.log('🔒 SECURITY: No current user - clearing all group context state');
      setAdminGroup(null);
      setAdminGroups([]);
      setActiveGroupId(null);
      setUserGroups([]);
      setLoading(false);
    }
  }, [currentUser?.id, currentUser?.email, currentUser?.role]); // 🔒 CRITICAL FIX: Also depend on role to ensure fresh data fetch

  // 🚨 CRITICAL FIX: Reset activeGroupId when currentUser changes (logout/login)
  useEffect(() => {
    if (!currentUser) {
      // User logged out - immediately clear all group state
      console.log('🔒 SECURITY: User logged out, clearing all group state');
      setAdminGroup(null);
      setAdminGroups([]);
      setActiveGroupId(null);
      setUserGroups([]);
      setError('');
    }
  }, [currentUser]);

  // Set a specific group as the active admin group - with security check
  const setActiveAdminGroup = (groupId) => {
    if (!groupId || !currentUser) return;
    
    // Only set active admin group if it belongs to the current admin
    const group = adminGroups.find(g => g.id === groupId && g.admin_id === currentUser.id);
    if (group) {
      setAdminGroup(group);
      setActiveGroupId(groupId);
    }
  };

  // Check if a group belongs to the current admin
  const isGroupOwnedByAdmin = (groupId) => {
    if (!currentUser || currentUser.role !== 'admin' || !groupId) return false;
    return adminGroups.some(g => g.id === groupId && g.admin_id === currentUser.id);
  };

  // Update admin group data
  const updateAdminGroup = async (groupId, groupData) => {
    try {
      setError('');
      
      // Additional security check - only allow updates for groups the admin owns
      if (!isGroupOwnedByAdmin(groupId)) {
        throw new Error('Not authorized to update this group');
      }
      
      const response = await groupService.updateGroup(groupId, groupData);
      
      if (response && response.data) {
        // Verify admin_id didn't change
        if (response.data.admin_id !== currentUser.id) {
          throw new Error('Group ownership cannot be changed');
        }
        
        // Update the active admin group with new data
        setAdminGroup(response.data);
        setActiveGroupId(groupId);
        
        // Also update this group in the adminGroups array
        setAdminGroups(prevGroups => 
          prevGroups.map(g => g.id === groupId ? response.data : g)
        );
      }
      
      return response;
    } catch (err) {
      setError(err.message || 'Failed to update group settings');
      throw err;
    }
  };

  // Add a newly created group immediately to context (for onboarding)
  const addNewAdminGroup = (groupData, expectedUserId = null) => {
    try {
      console.log('🎯 IMMEDIATE: Adding new admin group to context:', groupData);
      console.log('🎯 IMMEDIATE: Expected user ID:', expectedUserId);
      console.log('🎯 IMMEDIATE: Current user ID:', currentUser?.id);
      
      // Use expectedUserId if provided, otherwise fall back to currentUser
      const userIdToCheck = expectedUserId || currentUser?.id;
      
      // Verify this is for the expected admin
      if (groupData.admin_id !== userIdToCheck) {
        throw new Error(`Group ownership mismatch: expected ${userIdToCheck}, got ${groupData.admin_id}`);
      }
      
      // Immediately set this as the active admin group
      setAdminGroup(groupData);
      setActiveGroupId(groupData.id);
      
      // Add to adminGroups array
      setAdminGroups(prevGroups => [groupData, ...prevGroups]);
      
      // Mark that this admin now has communities
      setAdminHadCommunities(true);
      
      // Clear loading state
      setLoading(false);
      setError('');
      
      console.log('✅ IMMEDIATE: Successfully added new admin group to context');
      return true;
    } catch (err) {
      console.error('❌ IMMEDIATE: Failed to add new admin group:', err);
      setError(err.message || 'Failed to add new group to context');
      return false;
    }
  };

  // Get fresh data for admin group
  const refreshAdminGroup = async () => {
    try {
      setError('');
      console.log('🔄 REFRESHING admin groups for:', currentUser?.email);
      
      const response = await groupService.getAdminGroups();
      
      // 🚨 DEBUG: Log the exact response structure
      console.log('🔄 REFRESH: Full API response:', response);
      console.log('🔄 REFRESH: response.data:', response?.data);
      console.log('🔄 REFRESH: response.data.data:', response?.data?.data);
      console.log('🔄 REFRESH: Current user ID:', currentUser?.id);
      
      // 🚨 CRITICAL FIX: Handle the correct response structure
      // Server returns: {success: true, count: 1, data: [groups]}
      // NOT: {success: true, count: 1, data: {data: [groups]}}
      let allGroups = [];
      
      if (response && response.data) {
        // Check if response.data is the array directly
        if (Array.isArray(response.data)) {
          allGroups = response.data;
          console.log('🔄 REFRESH: Using response.data directly as array');
        } 
        // Check if response.data.data exists (nested structure)
        else if (response.data.data && Array.isArray(response.data.data)) {
          allGroups = response.data.data;
          console.log('🔄 REFRESH: Using response.data.data as array');
        }
        // If response.data is an object with other properties, check for the array
        else if (response.data && typeof response.data === 'object') {
          // Look for any array property in the response
          const arrayProp = Object.values(response.data).find(val => Array.isArray(val));
          if (arrayProp) {
            allGroups = arrayProp;
            console.log('🔄 REFRESH: Found array in response.data object');
          }
        }
      }
      
      console.log('🔄 REFRESH: Extracted groups array:', allGroups);
      console.log('🔄 REFRESH: Groups array length:', allGroups.length);
      
      if (allGroups && allGroups.length > 0) {
        console.log('🔄 REFRESH: All groups from API:', allGroups);
        
        // 🚨 CRITICAL FIX: Handle user ID mismatches more gracefully
        // Filter to only include groups the current admin owns
        const validAdminGroups = allGroups.filter(
          group => {
            // 🔒 SECURITY: Check if currentUser exists before accessing properties
            if (!currentUser || !currentUser.id) {
              console.warn('🚨 WARNING: currentUser is null during group filtering, skipping group:', group.id);
              return false;
            }
            
            const isMatch = group.admin_id === currentUser.id;
            console.log(`🔄 REFRESH: Checking group ${group.id}: admin_id=${group.admin_id}, current_user=${currentUser.id}, match=${isMatch}`);
            
            // 🔧 EMERGENCY: If no groups match, check if there's a user ID consistency issue
            if (!isMatch && allGroups.length === 1 && currentUser && currentUser.id) {
              console.warn('🚨 EMERGENCY: User ID mismatch detected! Group admin_id:', group.admin_id, 'vs Current user ID:', currentUser.id);
              console.warn('🚨 EMERGENCY: This might be a session inconsistency. Consider refreshing the user session.');
            }
            
            return isMatch;
          }
        );
        
        console.log('🔄 REFRESH: Found', validAdminGroups.length, 'valid admin groups');
        console.log('🔄 REFRESH: Valid admin groups:', validAdminGroups);
        
        // 🚨 EMERGENCY FALLBACK: If no valid groups but API returned groups, there's a user ID mismatch
        if (validAdminGroups.length === 0 && allGroups.length > 0) {
          // 🔒 SECURITY: Check if the issue is due to null currentUser
          if (!currentUser || !currentUser.id) {
            console.warn('🚨 WARNING: No valid groups found because currentUser is null');
            console.warn('🚨 WARNING: This might be a timing issue during authentication');
            console.warn('🚨 WARNING: Skipping group update, will retry when user data is available');
            return; // Don't update state, let it retry when currentUser is set
          }
          
          console.error('🚨 EMERGENCY: API returned groups but none match current user ID!');
          console.error('🚨 EMERGENCY: This indicates a session inconsistency problem.');
          console.error('🚨 EMERGENCY: All groups:', allGroups);
          console.error('🚨 EMERGENCY: Current user:', currentUser);
          
          // 🔧 NUCLEAR OPTION: Force reload to resync session
          console.error('🚨 NUCLEAR: Forcing page reload to fix session inconsistency...');
          setTimeout(() => {
            window.location.reload();
          }, 2000);
          
          return; // Don't update state with empty array
        }
        
        // Store all admin groups
        setAdminGroups(validAdminGroups);
        
        // If we have an active group ID, find and update that specific group
        // Only if the admin owns it
        if (activeGroupId) {
          const activeGroup = validAdminGroups.find(g => g.id === activeGroupId);
          if (activeGroup) {
            setAdminGroup(activeGroup);
            console.log('🔄 REFRESH: Updated active group:', activeGroup.name);
          } else if (validAdminGroups.length > 0) {
            // If active group not found or admin doesn't own it, use the first owned one
            setAdminGroup(validAdminGroups[0]);
            setActiveGroupId(validAdminGroups[0].id);
            console.log('🔄 REFRESH: Set new active group:', validAdminGroups[0].name);
          } else {
            setAdminGroup(null);
            setActiveGroupId(null);
            console.log('🔄 REFRESH: No valid groups found, cleared state');
          }
        } else if (validAdminGroups.length > 0) {
          // No active group, use the first one
          setAdminGroup(validAdminGroups[0]);
          setActiveGroupId(validAdminGroups[0].id);
          console.log('🔄 REFRESH: Set first group as active:', validAdminGroups[0].name);
        }
      } else {
        // 🚨 DEBUG: Log why no groups were found
        console.log('🔄 REFRESH: No groups found in response, clearing state');
        console.log('🔄 REFRESH: Reason analysis:');
        console.log('  - response exists:', !!response);
        console.log('  - response.data exists:', !!(response?.data));
        console.log('  - response.data is array:', Array.isArray(response?.data));
        console.log('  - response.data.data exists:', !!(response?.data?.data));
        console.log('  - response.data.data is array:', Array.isArray(response?.data?.data));
        console.log('  - allGroups.length:', allGroups?.length);
        
        setAdminGroups([]);
        setAdminGroup(null);
        setActiveGroupId(null);
      }
    } catch (err) {
      setError('Failed to refresh group data');
      console.error('Error refreshing admin group:', err);
    }
  };

  // Refresh user groups
  const refreshUserGroups = async () => {
    try {
      setError('');
      const response = await groupService.getUserGroups();
      if (response && response.data) {
        setUserGroups(response.data);
      }
    } catch (err) {
      setError('Failed to refresh user groups');
      console.error('Error refreshing user groups:', err);
    }
  };

  // Context value
  const value = {
    adminGroup,
    adminGroups,
    userGroups,
    loading,
    error,
    setActiveAdminGroup,
    updateAdminGroup,
    addNewAdminGroup,
    refreshAdminGroup,
    refreshUserGroups,
    isGroupOwnedByAdmin,
    adminHadCommunities,
    selectGroup,
    isPlatformAdmin,
  };

  return (
    <GroupContext.Provider value={value}>
      {children}
    </GroupContext.Provider>
  );
};

export default GroupContext;